/**
 * Exporta el reporte de libro de ingresos a Excel con múltiples hojas
 * @param data - Datos del reporte de ingresos
 * @param fileName - Nombre del archivo (opcional)
 */

type LineType = 'income' | 'expense' | 'conciliated' | 'wallet';
interface IncomeBookLine {
  folio: string;
  amountConciliated: number;
  amountInWallet: number;
  amountInWalletNet: number;
  amountLend: number;
  amountPlaced: number;
  amountPlacedNet: number;
  arrearIncome: number;
  businessIdentifier: string;
  businessName: string;
  comissionIncome: number;
  documentExtensionPriceIncome: number;
  documentExtensionCommissionIncome: number;
  documentOtherIncome: number;
  lineTypes: LineType[];
  documentResellExpense: number;
  expensesIncome: number;
  priceIncome: number;
  realAmountInWallet: number;
  realAmountInWalletNet: number;
  totalIncome: number;
  totalDocumentResellExpense: number;
  requestDate: string;
  requestId: string;
  requestType: string;
  requestTypeTranslated: string;
  stakeholderIdentifier: string;
  userOwnerId: number;
}

interface IncomeBookData {
  date: string;
  lines: IncomeBookLine[];
  totalRealWallet: number;
  totalRealWalletNet: number;
  totalWallet: number;
  totalWalletNet: number;
  totalPlaced: number;
  totalPlacedNet: number;
  totalConciliated: number;
  totalPriceIncome: number;
  totalArrearIncome: number;
  totalComissionIncome: number;
  totalExpensesIncome: number;
  totalDocumentExtensionPriceIncome: number;
  totalDocumentExtensionCommissionIncome: number;
  totalIncome: number;
  totalDocumentResellExpense: number;
}
export const exportIncomeBookToExcel = async (
  data: IncomeBookData[],
  fileName: string = 'reporte_ingresos.xlsx',
) => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();

  // Hoja 1: Resumen diario (tabla principal)
  const summaryWorksheet = workbook.addWorksheet('Resumen Diario');

  const summaryHeaders = [
    'Fecha',
    'Total colocado',
    'Total colocado neto',
    'Total Documento colocado',
    'Total Documento colocado neto',
    'Monto colocado',
    'Monto colocado neto',
    'Recaudado',
    'I. Devengo',
    'I. mora',
    'I. comisión',
    'I. Gastos',
    'I. Prorroga precio',
    'I. Prorroga comisión',
    'Ingreso total',
    'C. fondo',
  ];

  const summaryData = data.map((item: IncomeBookData) => [
    item.date,
    item.totalRealWallet,
    item.totalRealWalletNet,
    item.totalWallet,
    item.totalWalletNet,
    item.totalPlaced,
    item.totalPlacedNet,
    item.totalConciliated,
    item.totalPriceIncome,
    item.totalArrearIncome,
    item.totalComissionIncome,
    item.totalExpensesIncome,
    item.totalDocumentExtensionPriceIncome,
    item.totalDocumentExtensionCommissionIncome,
    item.totalIncome,
    item.totalDocumentResellExpense,
  ]);

  // Configurar formato de números para las columnas numéricas
  [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].forEach((col) => {
    summaryWorksheet.getColumn(col).numFmt = '#,##0';
  });

  // Agregar datos a la hoja de resumen
  [summaryHeaders, ...summaryData].forEach((rowData, rowIndex) => {
    const row = summaryWorksheet.addRow(rowData);
    if (rowIndex === 0) {
      // Estilo para el encabezado
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        summaryWorksheet.getColumn(cell.col).width = 18;
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' },
        };
      });
    }
  });

  // Calcular totales para el resumen
  const summaryTotals = data.reduce(
    (acc, item) => ({
      totalRealWallet: (acc.totalRealWallet || 0) + (item.totalRealWallet || 0),
      totalRealWalletNet:
        (acc.totalRealWalletNet || 0) + (item.totalRealWalletNet || 0),
      totalWallet: (acc.totalWallet || 0) + (item.totalWallet || 0),
      totalWalletNet: (acc.totalWalletNet || 0) + (item.totalWalletNet || 0),
      totalPlaced: (acc.totalPlaced || 0) + (item.totalPlaced || 0),
      totalPlacedNet: (acc.totalPlacedNet || 0) + (item.totalPlacedNet || 0),
      totalConciliated:
        (acc.totalConciliated || 0) + (item.totalConciliated || 0),
      totalPriceIncome:
        (acc.totalPriceIncome || 0) + (item.totalPriceIncome || 0),
      totalArrearIncome:
        (acc.totalArrearIncome || 0) + (item.totalArrearIncome || 0),
      totalComissionIncome:
        (acc.totalComissionIncome || 0) + (item.totalComissionIncome || 0),
      totalExpensesIncome:
        (acc.totalExpensesIncome || 0) + (item.totalExpensesIncome || 0),
      totalDocumentExtensionPriceIncome:
        (acc.totalDocumentExtensionPriceIncome || 0) +
        (item.totalDocumentExtensionPriceIncome || 0),
      totalDocumentExtensionCommissionIncome:
        (acc.totalDocumentExtensionCommissionIncome || 0) +
        (item.totalDocumentExtensionCommissionIncome || 0),
      totalIncome: (acc.totalIncome || 0) + (item.totalIncome || 0),
      totalDocumentResellExpense:
        (acc.totalDocumentResellExpense || 0) +
        (item.totalDocumentResellExpense || 0),
    }),
    {} as Partial<IncomeBookData>,
  );

  // Agregar una línea en blanco antes del total general
  summaryWorksheet.addRow([]);

  // Agregar fila de totales
  const summaryTotalRow = summaryWorksheet.addRow([
    'TOTAL GENERAL',
    summaryTotals.totalRealWallet || 0,
    summaryTotals.totalRealWalletNet || 0,
    summaryTotals.totalWallet || 0,
    summaryTotals.totalWalletNet || 0,
    summaryTotals.totalPlaced || 0,
    summaryTotals.totalPlacedNet || 0,
    summaryTotals.totalConciliated || 0,
    summaryTotals.totalPriceIncome || 0,
    summaryTotals.totalArrearIncome || 0,
    summaryTotals.totalComissionIncome || 0,
    summaryTotals.totalExpensesIncome || 0,
    summaryTotals.totalDocumentExtensionPriceIncome || 0,
    summaryTotals.totalDocumentExtensionCommissionIncome || 0,
    summaryTotals.totalIncome || 0,
    summaryTotals.totalDocumentResellExpense || 0,
  ]);

  // Estilo para la fila de totales
  summaryTotalRow.eachCell((cell) => {
    cell.font = { bold: true, size: 12 };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE6E6E6' },
    };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'double' },
    };
  });

  // Crear hojas por tipo de línea
  const allIncomeLines: Array<IncomeBookLine & { date: string }> = [];
  const allWalletLines: Array<IncomeBookLine & { date: string }> = [];
  const allExpenseLines: Array<IncomeBookLine & { date: string }> = [];
  const allConciliatedLines: Array<IncomeBookLine & { date: string }> = [];

  // Recolectar todas las líneas de todos los días
  data.forEach((dayData: IncomeBookData) => {
    if (dayData.lines) {
      dayData.lines.forEach((line: IncomeBookLine) => {
        const lineWithDate = {
          ...line,
          date: dayData.date,
        };

        // Agregar la línea a las pestañas correspondientes según sus tipos
        if (line.lineTypes.includes('income')) {
          allIncomeLines.push(lineWithDate);
        }
        if (line.lineTypes.includes('wallet')) {
          allWalletLines.push(lineWithDate);
        }
        if (line.lineTypes.includes('expense')) {
          allExpenseLines.push(lineWithDate);
        }
        if (line.lineTypes.includes('conciliated')) {
          allConciliatedLines.push(lineWithDate);
        }
      });
    }
  });

  // Función auxiliar para crear hojas por tipo de línea
  const createLineTypeWorksheet = (
    lineType: LineType,
    spanishName: string,
    lineData: Array<IncomeBookLine & { date: string }>,
  ) => {
    if (!lineData || lineData.length === 0) return;

    const worksheet = workbook.addWorksheet(spanishName);

    // Headers para las líneas detalladas
    const lineHeaders = [
      'Fecha',
      'Tipo de solicitud',
      'Nombre empresa',
      'RUT',
      'ID/Folio',
      'Cartera',
      'Cartera neto',
      'Monto colocado',
      'Monto colocado neto',
      'Recaudado',
      'Interés devengado',
      'Interés mora',
      'Monto comisión',
      'Gastos',
      'Ingreso total',
      'Costo fondo',
    ];

    // Agregar encabezados
    const headerRow = worksheet.addRow(lineHeaders);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      worksheet.getColumn(cell.col).width = 15;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDDDDDD' },
      };
    });

    // Configurar formato de números para las columnas numéricas
    [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].forEach((col) => {
      worksheet.getColumn(col).numFmt = '#,##0';
    });

    // Agregar datos de las líneas
    lineData.forEach((line) => {
      const rowData = [
        line.date,
        line.requestTypeTranslated || '',
        line.businessName || '',
        line.businessIdentifier || '',
        line.requestId || '',
        line.amountInWallet || 0,
        line.amountInWalletNet || 0,
        line.amountPlaced || 0,
        line.amountPlacedNet || 0,
        line.amountConciliated || 0,
        line.priceIncome || 0,
        line.arrearIncome || 0,
        line.comissionIncome || 0,
        line.expensesIncome || 0,
        line.totalIncome || 0,
        line.documentResellExpense || 0,
      ];

      worksheet.addRow(rowData);
    });

    // Calcular y agregar totales
    const totals = lineData.reduce(
      (acc, line) => ({
        amountInWallet: (acc.amountInWallet || 0) + (line.amountInWallet || 0),
        amountInWalletNet:
          (acc.amountInWalletNet || 0) + (line.amountInWalletNet || 0),
        amountPlaced: (acc.amountPlaced || 0) + (line.amountPlaced || 0),
        amountPlacedNet:
          (acc.amountPlacedNet || 0) + (line.amountPlacedNet || 0),
        amountConciliated:
          (acc.amountConciliated || 0) + (line.amountConciliated || 0),
        priceIncome: (acc.priceIncome || 0) + (line.priceIncome || 0),
        arrearIncome: (acc.arrearIncome || 0) + (line.arrearIncome || 0),
        comissionIncome:
          (acc.comissionIncome || 0) + (line.comissionIncome || 0),
        expensesIncome: (acc.expensesIncome || 0) + (line.expensesIncome || 0),
        totalIncome: (acc.totalIncome || 0) + (line.totalIncome || 0),
        documentResellExpense:
          (acc.documentResellExpense || 0) + (line.documentResellExpense || 0),
      }),
      {} as Partial<IncomeBookLine>,
    );

    // Agregar fila de totales
    const totalRow = worksheet.addRow([
      'TOTALES',
      '', // Tipo de solicitud
      '', // Nombre empresa
      '', // RUT
      '', // ID/Folio
      totals.amountInWallet || 0,
      totals.amountInWalletNet || 0,
      totals.amountPlaced || 0,
      totals.amountPlacedNet || 0,
      totals.amountConciliated || 0,
      totals.priceIncome || 0,
      totals.arrearIncome || 0,
      totals.comissionIncome || 0,
      totals.expensesIncome || 0,
      totals.totalIncome || 0,
      totals.documentResellExpense || 0,
    ]);

    // Estilo para la fila de totales
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEEEEEE' },
      };
    });

    // Agregar fila de totales por columna
    const columnTotals = [
      'TOTAL GENERAL',
      '', // Tipo de solicitud
      '', // Nombre empresa
      '', // RUT
      '', // ID/Folio
      totals.amountInWallet || 0,
      totals.amountInWalletNet || 0,
      totals.amountPlaced || 0,
      totals.amountPlacedNet || 0,
      totals.amountConciliated || 0,
      totals.priceIncome || 0,
      totals.arrearIncome || 0,
      totals.comissionIncome || 0,
      totals.expensesIncome || 0,
      totals.totalIncome || 0,
      totals.documentResellExpense || 0,
    ];

    // Agregar una línea en blanco antes del total general
    worksheet.addRow([]);

    const totalGeneralRow = worksheet.addRow(columnTotals);
    totalGeneralRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6E6' },
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'double' },
      };
    });
  };

  // Crear las hojas específicas con detalles
  createLineTypeWorksheet('income', 'Ingresos', allIncomeLines);
  createLineTypeWorksheet('wallet', 'Cartera', allWalletLines);
  createLineTypeWorksheet('expense', 'Gastos', allExpenseLines);
  createLineTypeWorksheet('conciliated', 'Conciliados', allConciliatedLines);

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();

  URL.revokeObjectURL(url);
};
